import React, { useEffect, useState } from "react";
import { useApp } from "@/state/AppContext";
import { api, rows } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PaymentQr } from "@/components/PaymentQr";
import { fromMinor, humanizeSlug, longDate, rupees } from "@/lib/format";
import { localized } from "@/theme/tenantTheme";

/**
 * A member's own memberships, on their activity page: what each is on, what it owes, and a UPI QR
 * to pay the shop. Read-only by design — the money moves out of band and the shop marks it
 * received (the no-gateway rule), so the member sees the QR, never a self-service charge.
 *
 * Self-hiding: a citizen who is nobody's member renders nothing, so the section never clutters the
 * page for the vast majority who have no memberships.
 */
function programLines(kind, body, library, lang) {
  const section = kind === "workout" ? body?.days?.[0] : body?.meals?.[0];
  return (section?.items ?? []).map((item) => {
    const ref = kind === "workout" ? item.exercise : item.food;
    if (ref?.custom) return ref.custom;
    if (!ref?.lib) return "";
    // `ref.lib` is a LIBRARY ITEM ID ("bench_press"), not display text (U5): resolve it against
    // the static library for the shown name, and only fall back to a humanised slug if the
    // library has dropped it since the plan was assigned.
    const found = library[kind === "workout" ? "exercises" : "foods"]?.get(ref.lib);
    return found ? localized(lang, found, "name") : humanizeSlug(ref.lib);
  });
}

export function MyMemberships() {
  const { t, lang } = useApp();
  const [items, setItems] = useState([]);
  const [payFor, setPayFor] = useState(null);
  const [plans, setPlans] = useState([]);
  const [library, setLibrary] = useState({});

  useEffect(() => {
    let alive = true;
    api
      .get("/membership/mine")
      .then((r) => {
        if (alive) setItems(rows(r.data));
      })
      .catch(() => {
        // A member's dues are a nice-to-have on this page, not its reason for being — a failed
        // fetch hides the section rather than erroring the whole activity view.
      });
    api
      .get("/programs/mine")
      .then((r) => {
        if (alive) setPlans(rows(r.data));
      })
      .catch(() => {
        // Same reasoning: an assigned plan is a bonus on this page, never its reason for being.
      });
    ["exercises", "foods"].forEach((name) => {
      api
        .get(`/programs/library/${name}`)
        .then((r) => {
          if (alive) {
            setLibrary((prev) => ({ ...prev, [name]: new Map(rows(r.data).map((i) => [i.id, i])) }));
          }
        })
        .catch(() => {
          // Names fall back to a humanised slug; the plan still shows.
        });
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!items.length) return null;

  return (
    <section className="mt-8 space-y-3" data-testid="my-memberships">
      <h2 className="font-heading text-xl font-bold">{t("memberships.title")}</h2>
      <ul className="space-y-2">
        {items.map((m) => {
          const myPlans = plans.filter((p) => p.business_id === m.business_id);
          return (
          <li key={m.id} className="rounded-2xl border border-border p-4" data-testid={`my-membership-${m.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-semibold">{m.business_name}</p>
                <p className="text-sm text-muted-foreground">
                  {m.plan_name} · {t("memberships.nextBill")} {longDate(m.next_bill_on, lang)}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {m.owed_minor_units > 0 ? (
                  <span className="text-sm font-semibold text-destructive" data-testid={`my-owed-${m.id}`}>
                    {t("memberships.owes")} {rupees({ minor_units: m.owed_minor_units })}
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground" data-testid={`my-settled-${m.id}`}>
                    {t("memberships.settled")}
                  </span>
                )}
                {m.owed_minor_units > 0 && m.upi_id && (
                  <Button
                    size="sm"
                    className="rounded-full"
                    onClick={() => setPayFor(payFor === m.id ? null : m.id)}
                    data-testid={`my-pay-${m.id}`}
                  >
                    {payFor === m.id ? t("memberships.hide") : t("memberships.pay")}
                  </Button>
                )}
              </div>
            </div>
            {payFor === m.id && m.upi_id && (
              <div className="mt-3 border-t border-border pt-3">
                <PaymentQr
                  upiId={m.upi_id}
                  payeeName={m.business_name}
                  amount={fromMinor(m.owed_minor_units)}
                  note={m.plan_name}
                  testId={`my-qr-${m.id}`}
                />
              </div>
            )}
            {myPlans.length > 0 && (
              <div className="mt-3 space-y-2 border-t border-border pt-3" data-testid={`my-programs-${m.id}`}>
                {myPlans.map((p) => (
                  <div key={p.id} data-testid={`my-program-${p.id}`}>
                    <p className="text-sm font-semibold">
                      {t(p.kind === "workout" ? "memberships.programWorkout" : "memberships.programDiet")} · {p.title}
                    </p>
                    <ul className="mt-1 text-sm text-muted-foreground">
                      {programLines(p.kind, p.body, library, lang).map((line, i) => (
                        <li key={i}>• {line}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </li>
          );
        })}
      </ul>
    </section>
  );
}
